// TODO: OSBSOLETE!


/* eslint-disable prefer-arrow/prefer-arrow-functions */
/**
 * This module contains style definitions for features inside the CrodwFlow map
 * as well as several static helper functions for various map operations.
 */


import { Feature } from 'ol';
import { default as FillPattern } from 'ol-ext/style/FillPattern';
import { LineString, Polygon } from 'ol/geom';
import { SelectEvent } from 'ol/interaction/Select';
import { Fill, Stroke, Style, Text } from 'ol/style';
import { v4 as uuidv4 } from 'uuid';

// Feature flag keys
const USE_SELECTED_ROI_STYLE = 'useSelectedRoiStyle';
const CURRENT_PEOPLE_COUNT = 'currentPeopleCount';

// Internal styles
const roiDefaultFill = new Fill({ color: [75, 168, 255, 0.75] });
const roiSelectedFill = new Fill({ color: [61, 255, 246, 0.75] });
const roiLabelText = new Text({
  font: '13px Roboto, "Helvetica Neue", sans-serif',
  fill: new Fill({ color: [255, 255, 255, 0.9] }),
  stroke: new Stroke({ color: [0, 0, 0, 0.8], width: 4 }),
  text: ''
});


// ====================== EXPORTED STYLES ======================

export const sensorSiteStyle = new Style({
  fill: new Fill({ color: [250, 210, 25, 0.5] }),
});

export const blindSpotStyle = new Style({
  stroke: new Stroke({
    color: [0, 0, 0, 1],
    width: 1
  }),
  fill: new FillPattern({
    color: [0, 0, 0, 0.75],
    pattern: 'hatch',
    size: 2,
    spacing: 5
  })
});

export const edgeDefaultStyle = new Style({
  stroke: new Stroke({ color: [88, 87, 255, 1], width: 7 }),
  zIndex: 50
});

export const edgeSelectedStyle = new Style({
  stroke: new Stroke({ color: [61, 255, 246, 1], width: 7 }),
  zIndex: 100
});


// ====================== EXPORTED FUNCTIONS ======================

/**
 * Extracts edges from an ROI polygon. Will set edgeIds if provided.
 */
export function extractEdgesFromRoi(roiFeature: Feature, edgeIds = []): Feature[] {
  const roiPolygon = roiFeature.getGeometry() as Polygon;
  const coordinates = roiPolygon.getLinearRing(0).getCoordinates(); // Get coordinates of the exterior ring

  const edges: Feature[] = [];
  for (let i = 0; i < coordinates.length - 1; i++) {
    const edge = new Feature(new LineString([coordinates[i], coordinates[i + 1]]));
    edge.setId(edgeIds[i] == null ? 'urn:ngsi-ld:CrowdFlowObserved:' + uuidv4() : edgeIds[i]);
    edge.setStyle(edgeDefaultStyle);
    edges.push(edge);
  }

  return edges;
}

export function isRoiFeature(feature: Feature): boolean {
  return feature && ('' + feature.getId()).includes('CrowdFlowROI');
}

/**
 * OpenLayers StyleFunction for ROI feature objects. Sets both default
 * and selected styles based on an inernal flag.
 */
export function roiStyleFunction(roiFeature: Feature, resolution?: number): Style {
  return new Style({
    fill: roiFeature.get(USE_SELECTED_ROI_STYLE) ? roiSelectedFill : roiDefaultFill,
    text: roiLabelText.clone()  // must be a clone, otherwise all ROIs will have the same label
  });
}

export function updateRoiText(roiFeature: Feature, peopleCount: number): void {
  roiFeature.set(CURRENT_PEOPLE_COUNT, peopleCount);
  updateRoiStyleOrText(roiFeature);
}

/**
 * Sets styles for all ROIs to selected/default based on a SelectEvent.
 */
export function updateRoiStyles(selectEvent: SelectEvent) {
  selectEvent.deselected.forEach(feature => {
    feature.set(USE_SELECTED_ROI_STYLE, false);
    updateRoiStyleOrText(feature);
  });
  selectEvent.selected.forEach(feature => {
    feature.set(USE_SELECTED_ROI_STYLE, true);
    updateRoiStyleOrText(feature);
  });
}


// ====================== INTERNAL FUNCTIONS ======================

function updateRoiStyleOrText(roiFeature: Feature): void {
  if (!isRoiFeature(roiFeature)) { return; }

  const newStyle = roiStyleFunction(roiFeature);
  const amount = roiFeature.get(CURRENT_PEOPLE_COUNT) ?? 'N/A';
  newStyle.getText().setText(`\uD83D\uDC64 ${amount}`);
  roiFeature.setStyle(newStyle);
}
